import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Snackbar,
  Chip,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { AdminPanelSettings, Visibility, Cancel } from '@mui/icons-material';
import { useAuth } from './AuthContext';
import { API_URL } from './constants';
import axios from 'axios';

const AdminPanel = () => {
  const { isAdmin, adminViewProjectKey, setAdminView, clearAdminView, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [usersByProject, setUsersByProject] = useState({});
  const [projectKeys, setProjectKeys] = useState([]);
  const [selectedProjectKey, setSelectedProjectKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data.users || []);
      setUsersByProject(response.data.users_by_project || {});
      setProjectKeys(response.data.project_keys || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      setMessage('Failed to load users. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAs = async (projectKey) => {
    if (!projectKey) {
      clearAdminView();
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/admin/view-as`,
        { project_key: projectKey },
        { withCredentials: true }
      );
      
      setAdminView(projectKey);
      setMessage(`Now viewing as: ${projectKey}`);
      setMessageType('success');
    } catch (error) {
      console.error('Failed to set admin view:', error);
      setMessage('Failed to set admin view. Please try again.');
      setMessageType('error');
    }
  };

  const handleSelectChange = (event) => {
    const projectKey = event.target.value;
    setSelectedProjectKey(projectKey);
    handleViewAs(projectKey);
  };

  if (!isAdmin) {
    console.log('AdminPanel: User is not admin, returning null');
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Admin panel is only available to administrators.
        </Alert>
      </Box>
    );
  }

  const currentProjectKey = adminViewProjectKey || user?.project_key || 'none';

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto', mb: 4 }}>
      <Card sx={{ border: '2px solid', borderColor: 'warning.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AdminPanelSettings color="warning" />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Admin Panel
            </Typography>
            <Chip label="Admin Mode" color="warning" size="small" sx={{ ml: 'auto' }} />
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Admin View Mode:</strong> You can view the application as if you were a user with a different project_key.
              This allows you to see what modules and features other users see.
            </Typography>
          </Alert>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              View As Project Key
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a project_key to view the application as that user type. The page will reload with the selected view.
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Project Key to View As</InputLabel>
              <Select
                value={selectedProjectKey || currentProjectKey}
                label="Select Project Key to View As"
                onChange={handleSelectChange}
              >
                <MenuItem value="">
                  <em>View as yourself (original)</em>
                </MenuItem>
                {projectKeys.map((key) => (
                  <MenuItem key={key} value={key}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {adminViewProjectKey && (
              <Alert 
                severity="warning" 
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setSelectedProjectKey('');
                      handleViewAs('');
                    }}
                    startIcon={<Cancel />}
                  >
                    Clear
                  </Button>
                }
              >
                Currently viewing as: <strong>{adminViewProjectKey}</strong>
              </Alert>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="h6" gutterBottom>
              Users by Project Key
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Overview of all users grouped by their project_key:
            </Typography>

            {loading ? (
              <Typography>Loading users...</Typography>
            ) : (
              <Grid container spacing={2}>
                {Object.entries(usersByProject).map(([projectKey, projectUsers]) => (
                  <Grid item xs={12} sm={6} md={4} key={projectKey}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {projectKey}
                      </Typography>
                      <Chip 
                        label={`${projectUsers.length} user${projectUsers.length !== 1 ? 's' : ''}`} 
                        size="small" 
                        sx={{ mb: 1 }}
                      />
                      <List dense>
                        {projectUsers.slice(0, 5).map((u) => (
                          <ListItem key={u.id} sx={{ py: 0.5, px: 0 }}>
                            <ListItemText
                              primary={u.email}
                              secondary={u.name || 'No name'}
                              primaryTypographyProps={{ fontSize: '0.875rem' }}
                              secondaryTypographyProps={{ fontSize: '0.75rem' }}
                            />
                          </ListItem>
                        ))}
                        {projectUsers.length > 5 && (
                          <ListItem sx={{ py: 0.5, px: 0 }}>
                            <ListItemText
                              primary={`... and ${projectUsers.length - 5} more`}
                              primaryTypographyProps={{ fontSize: '0.75rem', fontStyle: 'italic' }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={!!message}
        autoHideDuration={6000}
        onClose={() => setMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setMessage('')} severity={messageType} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPanel;

